import { Check, X, Crown, AlertTriangle } from "lucide-react";
import { useState, useEffect } from "react";

const Comparison = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const section = document.getElementById('comparison');
    if (section) observer.observe(section);

    return () => observer.disconnect();
  }, []);

  const comparisons = [
    { 
      feature: "Access", 
      others: "Anyone can join", 
      vcm: "Exclusive, Invite-Only",
      highlight: true,
      vcmIcon: Crown
    },
    { 
      feature: "Earnings", 
      others: "20-30% platform fees", 
      vcm: "0% Fees—Ever",
      highlight: true 
    },
    { 
      feature: "Commitment", 
      others: "Long-term contracts", 
      vcm: "No Contracts, Total Freedom" 
    },
    { 
      feature: "Support", 
      others: "Slow, generic responses", 
      vcm: "24/7 VIP, Dedicated Team" 
    },
    { 
      feature: "TikTok Partnership", 
      others: "Basic access", 
      vcm: "Direct, Premium Partnership"
    },
    { 
      feature: "Community", 
      others: "Crowded, open Discord", 
      vcm: "Curated Elite Creator Circle" 
    },
    { 
      feature: "Growth Guidance", 
      others: "One-size-fits-all webinars", 
      vcm: "Personal 1:1 Strategy Coaching" 
    },
    { 
      feature: "Brand Deals", 
      others: "Shared, low-priority", 
      vcm: "Priority Placement, Top Brands" 
    },
  ];

  return (
    <section id="comparison" className="py-24 md:py-32 bg-background relative">
      {/* Background accent */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] bg-primary/3 rounded-full blur-[200px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="text-sm font-semibold text-primary uppercase tracking-widest mb-3">
            The VCM Elite Standard
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            Why Ambitious Creators <span className="gradient-text">Leave the Crowd for VCM</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Most MCNs treat you like a number. At VCM, you're handpicked for greatness—and supported like family. Here's how we set the bar higher:
          </p>
        </div>

        {/* Comparison Grid */}
        <div className={`max-w-5xl mx-auto mb-16 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Header Row */}
          <div className="grid grid-cols-3 gap-4 mb-4 px-6">
            <div className="text-sm font-semibold text-muted-foreground uppercase tracking-wider"></div>
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-destructive/10 border border-destructive/20">
                <AlertTriangle className="w-4 h-4 text-destructive/70" />
                <span className="text-sm font-semibold text-destructive/80">Other MCNs</span>
              </div>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/30">
                <Crown className="w-4 h-4 text-primary" />
                <span className="text-sm font-semibold text-primary">VCM Elite (Invite-Only)</span>
              </div>
            </div>
          </div>
          
          {/* Comparison Rows */}
          <div className="space-y-2">
            {comparisons.map((item, index) => (
              <div
                key={index}
                className={`grid grid-cols-3 gap-4 p-5 rounded-xl transition-all duration-300 ${
                  item.highlight 
                    ? 'bg-gradient-to-r from-primary/10 via-secondary/5 to-accent/10 border border-primary/20' 
                    : 'bg-card/50 hover:bg-card border border-transparent hover:border-border/50'
                }`}
                style={{ 
                  transitionDelay: `${100 * index}ms`,
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(10px)'
                }}
              >
                <div className="font-semibold text-foreground flex items-center">{item.feature}</div>
                <div className="flex items-center justify-center gap-2">
                  <X className="w-5 h-5 text-destructive/70 flex-shrink-0" />
                  <span className="text-muted-foreground text-sm text-center">{item.others}</span>
                </div>
                <div className="flex items-center justify-center gap-2">
                  {item.vcmIcon ? (
                    <item.vcmIcon className="w-5 h-5 text-primary flex-shrink-0" />
                  ) : (
                    <Check className="w-5 h-5 text-accent flex-shrink-0" />
                  )}
                  <span className="text-foreground font-semibold text-sm text-center">{item.vcm}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Savings Calculator */}
        <div className={`max-w-4xl mx-auto transition-all duration-700 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="p-8 md:p-12 rounded-3xl bg-card border border-border relative overflow-hidden">
            {/* Background glow */}
            <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-accent/10 rounded-full blur-[100px]" />
            
            <div className="relative z-10">
              <div className="text-center mb-10">
                <h3 className="text-2xl md:text-3xl font-bold mb-2">
                  💰 Calculate Your <span className="gradient-text">Lost Earnings</span>
                </h3>
                <p className="text-muted-foreground">
                  Based on $100,000 in yearly diamond earnings
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="p-8 rounded-2xl bg-background border border-destructive/20 relative">
                  <div className="absolute top-4 right-4">
                    <AlertTriangle className="w-5 h-5 text-destructive/50" />
                  </div>
                  <div className="text-sm text-muted-foreground mb-2 uppercase tracking-wider">Other MCNs (25% fee)</div>
                  <div className="text-4xl md:text-5xl font-bold text-muted-foreground line-through decoration-destructive/50">$75,000</div>
                  <div className="text-sm text-destructive/70 mt-2 font-medium">You lose $25,000/year</div>
                </div>
                <div className="p-8 rounded-2xl bg-gradient-to-br from-primary/10 to-accent/10 border border-primary/30 relative">
                  <div className="absolute top-4 right-4">
                    <Crown className="w-5 h-5 text-primary" />
                  </div>
                  <div className="text-sm text-primary mb-2 uppercase tracking-wider font-semibold">VCM Elite (0% fee)</div>
                  <div className="text-4xl md:text-5xl font-bold text-foreground">$100,000</div>
                  <div className="text-sm text-accent mt-2 font-medium">You keep every cent</div>
                </div>
              </div>
              
              <div className="mt-10 text-center">
                <div className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-accent/10 border border-accent/30 mb-10">
                  <span className="text-2xl md:text-3xl font-bold text-accent">+$25,000</span>
                  <span className="text-foreground font-medium">more in your pocket each year</span>
                </div>
                
                <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-4">
                  At VCM, you're more than a username—you're part of a vetted, high-performance network built for creators who want it all: influence, earnings, and the kind of support you can't find anywhere else.
                </p>
                <p className="text-xl font-semibold text-foreground mb-8">
                  Ready to join the few who lead, not follow?
                </p>
                <a 
                  href="/apply" 
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-primary via-secondary to-accent text-white font-semibold text-lg shadow-[0_0_30px_rgba(217,70,239,0.4)] hover:shadow-[0_0_50px_rgba(217,70,239,0.6)] hover:scale-105 transition-all duration-300"
                >
                  Request Your Invite
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Comparison;
